import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Input,
} from "@fluentui/react-components";
import { useQueryClient } from "@tanstack/react-query";

// COmponents
import { GenericComboBox } from "../../../shared/components/GenericComboBox";
import { ErrorMessagesBar } from "../../../shared/components/ErrorMessagesBar";

// Business logic
import { ReadTicketStatuses } from "../services/ticketApi";
import { readUsers } from "../../users/services/userApi";
import { useItemQuery } from "../../../shared/hooks/useItemQuery";

const CiFiltersPanel = ({ isOpen, setIsOpen, queryParameters, setQueryParameters, ...props }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Errors
  const [errors, setErrors] = useState([]);

  // Data
  const { data: agentsData = [], error: agentsError } = useItemQuery(
    [`agents`],
    () => readUsers({ is_agent: 1 }),
    true,
  );
  const { data: ticketStatusesData = [], error: ticketStatusesError } = useItemQuery(
    [`ticket_statuses`],
    () => ReadTicketStatuses(),
    true,
  );
  const { data: usersData = [], error: usersError } = useItemQuery([`users`], () => readUsers({}), true);
  const types = [
    { id: "INC", label: "Incident" },
    { id: "SRQ", label: "Service Request" },
  ];

  // Effects
  useEffect(() => {
    const newErrors = [];
    if (agentsError) {
      newErrors.push({ message: agentsError?.message || t("generic.error"), type: "error", details: agentsError });
    }
    if (ticketStatusesError) {
      newErrors.push({
        message: ticketStatusesError?.message || t("generic.error"),
        type: "error",
        details: ticketStatusesError,
      });
    }
    if (usersError) {
      newErrors.push({ message: usersError?.message || t("generic.error"), type: "error", details: usersError });
    }
    if (newErrors.length > 0) {
      setErrors((prevErrors) => [...prevErrors, ...newErrors]);
    }
  }, [agentsError, ticketStatusesError, usersError, t]);

  // Sync queryParameters from URL when drawer opens
  useEffect(() => {
    if (isOpen) {
      const params = new URLSearchParams(location.search);
      setQueryParameters({
        type: params.get("type") || null,
        status_id: params.get("status_id") ? Number(params.get("status_id")) : null,
        req_id: params.get("req_id") ? Number(params.get("req_id")) : null,
        agent_id: params.get("agent_id") ? Number(params.get("agent_id")) : null,
      });
    }
  }, [isOpen, location.search, setQueryParameters]);

  // Handlers
  const handleApplyFilters = () => {
    const params = new URLSearchParams();

    if (queryParameters.type) params.append("type", queryParameters.type);
    if (queryParameters.status_id) params.append("status_id", queryParameters.status_id);
    if (queryParameters.req_id) params.append("req_id", queryParameters.req_id);
    if (queryParameters.agent_id) params.append("agent_id", queryParameters.agent_id);

    navigate({ search: params.toString() }, { replace: true });
    setIsOpen(false);
  };

  const handleClearFilters = () => {
    setQueryParameters({ type: null, status_id: null, req_id: null, agent_id: null });
    navigate({ search: "" }, { replace: true });
    setIsOpen(false);
  };

  return (
    <Drawer
      position="end"
      open={isOpen}
      onOpenChange={(event, data) => setIsOpen(data.open)}
      size="small"
      modalType="non-modal"
      {...props}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              icon={<Dismiss24Regular />}
              aria-label="Close"
              onClick={() => setIsOpen(false)}
            />
          }
        >
          {t("generic.filters")}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <ErrorMessagesBar errors={errors} onDismiss={() => setErrors([])} />
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1rem" }}>
          <Field label={t("generic.type")}>
            <GenericComboBox
              items={types}
              value={queryParameters?.type}
              valueKey="id"
              displayKey="label"
              onOptionSelect={(newType) => setQueryParameters((prev) => ({ ...prev, type: newType }))}
            />
          </Field>
          <Field label={t("generic.status")}>
            <GenericComboBox
              items={ticketStatusesData || []}
              value={queryParameters?.status_id}
              valueKey="id"
              displayKey="name"
              onOptionSelect={(newStatusId) => setQueryParameters((prev) => ({ ...prev, status_id: newStatusId }))}
            />
          </Field>
          <Field label={t("generic.requester")}>
            <GenericComboBox
              items={usersData || []}
              value={queryParameters?.req_id}
              valueKey="id"
              displayKey="name"
              onOptionSelect={(newUserId) => setQueryParameters((prev) => ({ ...prev, req_id: newUserId }))}
            />
          </Field>
          <Field label={t("generic.agent")}>
            <GenericComboBox
              items={agentsData || []}
              value={queryParameters?.agent_id}
              valueKey="id"
              displayKey="name"
              onOptionSelect={(newAgentId) => setQueryParameters((prev) => ({ ...prev, agent_id: newAgentId }))}
            />
          </Field>
        </div>
      </DrawerBody>
      <DrawerFooter>
        <div style={{ display: "flex", gap: "1rem", width: "100%" }}>
          <Button appearance="primary" onClick={handleApplyFilters} style={{ flex: 1 }}>
            {t("generic.apply")}
          </Button>
          <Button appearance="secondary" onClick={handleClearFilters} style={{ flex: 1 }}>
            {t("generic.reset")}
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};

export default CiFiltersPanel;
